import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  IconButton,
  Grid,
  Chip,
  CircularProgress
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Close as CloseIcon } from '@mui/icons-material';
import { Order, Product } from '../types';
import DatabaseService from '../services/DatabaseService';
import closeIcon from '../assets/closeIcon.png';

interface OrderConfirmationProps {
  open: boolean;
  onClose: () => void;
  order: Order | null;
}

const OrderConfirmation: React.FC<OrderConfirmationProps> = ({ open, onClose, order }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && order) {
      loadProducts();
    }
  }, [open, order]);

  const loadProducts = async () => {
    if (!order) return;
    setLoading(true);
    try {
      const productsData = await DatabaseService.getProducts();
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProductName = (productId: string): string => {
    const product = products.find(p => p.id === productId);
    return product ? product.name : productId;
  };

  if (!order) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '16px',
          m: isMobile ? 0 : 2,
          backgroundColor: '#F5F5F5',
        }
      }}
    >
      <style>{`
        .custom-cart-btn {
          background-color: #D0006F !important;
          color: white !important;
          border: none !important;
          border-radius: 2rem !important;
          padding: 0.75rem 2rem !important;
          font-size: 1rem !important;
          font-weight: bold !important;
          text-transform: none !important;
          cursor: pointer !important;
          transition: background 0.2s !important;
        }
        .custom-cart-btn:hover,
        .custom-cart-btn:focus {
          background-color: #B8005C !important;
          outline: none !important;
        }
        .custom-cart-btn:disabled {
          background-color: #F0A6C7 !important;
          color: white !important;
          cursor: not-allowed !important;
        }
      `}</style>
      {/* Custom Close Button */}
      <Box
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: '1rem',
          right: '1rem',
          cursor: 'pointer',
          zIndex: 1000,
          backgroundColor: 'white',
          borderRadius: '50%',
          width: '2.5rem',
          height: '2.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
        }}
      >
        <img
          src={closeIcon}
          alt="closeIcon"
          style={{ width: '1.5rem', height: '1.5rem' }}
        />
      </Box>
      {/* Header Section */}
      <Box
        sx={{
          backgroundColor: 'white',
          p: { xs: '1rem', sm: '2rem' },
          textAlign: 'left',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 'bold',
            fontSize: { xs: '2rem', sm: '3rem', md: '3.5rem' },
            color: '#131B5C',
            mb: '0.5rem',
            textAlign: 'left',
          }}
        >
          Order Confirmation
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Manrope, sans-serif',
            fontSize: { xs: '1rem', sm: '1.25rem' },
            color: '#666',
            mb: '2rem',
            textAlign: 'left',
          }}
        >
          Thank you for your order!
        </Typography>
        <Chip
          label={`Order ID: ${order.id}`}
          color="primary"
          sx={{ mt: 1, fontWeight: 'bold', fontSize: '1rem', background: '#EDF9FF', color: '#131B5C' }}
        />
        {order.customerInfo.email && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            A confirmation email has been sent to <strong>{order.customerInfo.email}</strong>.
            Please check your inbox (and spam folder) for order details.
          </Typography>
        )}
      </Box>
      {/* Main Content Area */}
      <DialogContent
        sx={{
          p: { xs: '0.5rem', sm: '2rem' },
          backgroundColor: '#F5F5F5',
          width: '100%',
          boxSizing: 'border-box',
          minWidth: 0,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Top Row: Customer Info and Order Summary */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
            {/* Customer Info */}
            <Box sx={{ flex: 1, backgroundColor: 'white', borderRadius: '1rem', p: { xs: '1rem', sm: '2rem' }, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 'bold', fontSize: '1.5rem', color: '#020742', mb: '1rem' }}>
                Customer Information
              </Typography>
              <Typography variant="body1">{order.customerInfo.name}</Typography>
              <Typography variant="body2">{order.customerInfo.email}</Typography>
              <Typography variant="body2">{order.customerInfo.phone}</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2">{order.customerInfo.address}</Typography>
              <Typography variant="body2">{order.customerInfo.city}, {order.customerInfo.postalCode}</Typography>
              <Typography variant="body2">{order.customerInfo.country}</Typography>
            </Box>
            {/* Order Summary */}
            <Box sx={{ flex: 1, backgroundColor: 'white', borderRadius: '1rem', p: { xs: '1rem', sm: '2rem' }, minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 'bold', fontSize: '1.5rem', color: '#020742', mb: '1rem' }}>
                Order Summary
              </Typography>
              <Typography variant="body2" gutterBottom>
                Order Date: {new Date(order.createdAt).toLocaleString()}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Status:
              </Typography>
              <Chip size="small" label="Pending" color="warning" sx={{ mt: 1 }} />
            </Box>
          </Box>
          {/* Order Items */}
          <Box sx={{ backgroundColor: 'white', borderRadius: '1rem', p: { xs: '1rem', sm: '2rem' }, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#131B5C', fontSize: '1.2rem', mb: 2 }}>Order Items</Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {order.products.map((product) => (
                    <TableRow key={product.productId}>
                      <TableCell>{getProductName(product.productId)}</TableCell>
                      <TableCell align="right">{product.quantity}</TableCell>
                      <TableCell align="right">{product.price.toFixed(3)} TND</TableCell>
                      <TableCell align="right">{(product.price * product.quantity).toFixed(3)} TND</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
          {/* Order Summary with Delivery Fee */}
          <Box sx={{ backgroundColor: '#EDF9FF', borderRadius: '1rem', p: { xs: '1rem', sm: '1.5rem' }, mt: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#131B5C', fontSize: '1.25rem', mb: 1 }}>Order Summary</Typography>
            
            {/* Calculate subtotal and delivery fee */}
            {(() => {
              const subtotal = order.products.reduce((sum, product) => sum + (product.price * product.quantity), 0);
              const deliveryFee = order.totalAmount - subtotal;
              
              return (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body1" sx={{ color: '#131B5C' }}>Subtotal:</Typography>
                    <Typography variant="body1" sx={{ color: '#131B5C' }}>{subtotal.toFixed(2)} TND</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body1" sx={{ color: '#131B5C' }}>Delivery Fee:</Typography>
                    <Typography variant="body1" sx={{ color: '#131B5C' }}>
                      {deliveryFee > 0 ? `${deliveryFee.toFixed(2)} TND` : 'FREE'}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#131B5C', fontSize: '1.25rem' }}>Total:</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#131B5C', fontSize: '1.25rem' }}>{order.totalAmount.toFixed(2)} TND</Typography>
                  </Box>
                </>
              );
            })()}
          </Box>
          {/* Success Message */}
          <Box sx={{ mt: 2, p: 2, bgcolor: '#EDF9FF', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: '#131B5C', fontWeight: 'bold' }}>
              <CheckCircleIcon fontSize="small" sx={{ mr: 1, color: '#00C853' }} />
              Your order has been successfully placed.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, color: '#131B5C' }}>
              If you have any questions about your order, please contact our customer service at <strong>support@tachafy.com</strong>.
            </Typography>
          </Box>
          {/* Custom Continue Shopping Button */}
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <button className="custom-cart-btn" onClick={onClose} style={{ width: isMobile ? '100%' : 'auto' }}>
              Continue Shopping
            </button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OrderConfirmation; 